import {
  BookOpenText,
  Brain,
  Clock,
  History,
  ListOrdered,
  MinusCircle,
  Play,
  PlusCircle,
  Shuffle,
  Target,
} from 'lucide-react';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { mensajeDe } from '@/api/errors';
import { BotonBiColor, TituloBloque } from '@/components/BloquesConsalud';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { AVATARES } from '@/lib/avatares';
import { Avatar } from '@/components/Avatar';
import { fechaLarga } from '@/lib/formato';
import { useStagger } from '@/lib/motion';
import { cn } from '@/lib/utils';

import { Cargando, ErrorCarga } from '../curso/componentes';

import { colorCalificacion, etiquetaCategoria } from './comun';
import {
  useCrearSimulador,
  useHistorialSimulador,
  useImportarSimulador,
  useSimuladorConfig,
  type HistoryItem,
} from './queries';

type Modo = 'enarm' | 'entrenamiento';
type Categoria = 'enarm' | 'aleatorio' | 'por-especialidad';

const MODOS: { key: Modo; titulo: string; texto: string; icono: typeof Target }[] = [
  {
    key: 'enarm',
    titulo: 'ENARM',
    texto: 'Al finalizar la prueba visualizarás la respuesta correcta y su justificación.',
    icono: Brain,
  },
  {
    key: 'entrenamiento',
    titulo: 'Entrenamiento',
    texto:
      'Al contestar un caso clínico se te mostrará la respuesta correcta y su justificación de manera inmediata.',
    icono: Clock,
  },
];
const CATEGORIAS: { key: Categoria; titulo: string; texto: ReactNode; icono: typeof Target }[] = [
  {
    key: 'enarm',
    titulo: 'ENARM',
    texto: (
      <>
        Tus casos clínicos se distribuyen de la siguiente manera:
        <br />
        30 % de Medicina Interna
        <br />
        25 % de Ginecología
        <br />
        25 % de Pediatría
        <br />
        20 % de Cirugía
      </>
    ),
    icono: BookOpenText,
  },
  {
    key: 'aleatorio',
    titulo: 'Aleatorio',
    texto: 'Si eliges esta categoría, tus casos clínicos se elegirán al azar.',
    icono: Shuffle,
  },
  {
    key: 'por-especialidad',
    titulo: 'Por especialidad',
    texto: 'Tus casos clínicos se distribuyen de acuerdo a tu selección.',
    icono: ListOrdered,
  },
];

/** Configurador del simulador (port de `inicio/simulador-v2`). */
export function SimuladorPage() {
  const { data: config, error, isPending } = useSimuladorConfig();
  const crear = useCrearSimulador();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [modo, setModo] = useState<Modo>('enarm');
  const [categoria, setCategoria] = useState<Categoria>('enarm');
  const [paso, setPaso] = useState(0);
  const [especialidades, setEspecialidades] = useState<number[]>([]);
  const [elegir, setElegir] = useState(false);
  const historial = params.get('historial') === '1';
  const root = useRef<HTMLDivElement>(null);
  useStagger(root, '[data-card]', { step: 60, deps: [config === undefined] });

  const disponibles = useMemo(() => {
    if (!config) return 0;
    const todas = config.areas.flatMap((a) => a.specialties);
    return todas.filter((s) => especialidades.includes(s.id)).reduce((n, s) => n + s.available, 0);
  }, [config, especialidades]);

  if (isPending) return <Cargando alto="h-96" />;
  if (error) return <ErrorCarga error={error} />;

  const demo = config.demo;
  const casos = config.caseSteps[Math.min(paso, config.caseSteps.length - 1)] ?? 5;
  const modoEf: Modo = demo ? 'entrenamiento' : modo;
  const catEf: Categoria = demo ? 'aleatorio' : categoria;
  const faltan =
    catEf === 'por-especialidad' && (especialidades.length === 0 || disponibles < casos);

  const iniciar = () => {
    crear.mutate(
      {
        mode: modoEf,
        category: catEf,
        cases: casos,
        ...(catEf === 'por-especialidad' ? { specialties: especialidades } : {}),
      },
      {
        onSuccess: (r) => void navigate(`/simulador/${String(r.id)}`),
        onError: (e) => toast.error(mensajeDe(e)),
      },
    );
  };

  return (
    <div ref={root} className="space-y-6">
      <h1 className="titulo-seccion">
        <span className="subrayado-verde">Simulador ENARM</span>
      </h1>

      <div className="grid gap-x-6 gap-y-14 pt-10 lg:grid-cols-3">
        <section data-card className="panel-vidrio space-y-4 text-center">
          <TituloBloque avatar={AVATARES.doctor}>1. Elige un MODO</TituloBloque>
          {MODOS.map((m) => (
            <div key={m.key} className="space-y-2 pt-2">
              <BotonBiColor
                icono={<m.icono size={30} />}
                activo={modoEf === m.key}
                disabled={demo && m.key === 'enarm'}
                exclusivo={demo && m.key === 'enarm'}
                onClick={() => setModo(m.key)}
              >
                {m.titulo}
              </BotonBiColor>
              <p className="mx-auto max-w-[260px] text-sm text-texto-2">{m.texto}</p>
            </div>
          ))}
        </section>

        <section data-card className="panel-vidrio flex flex-col items-center gap-5 text-center">
          <TituloBloque icono={<Target size={18} />}>2. Elige un RETO</TituloBloque>

          {demo && (
            <div className="relative w-full max-w-[260px]">
              <Avatar src={AVATARES.doctoraSenala} className="absolute -top-12 -right-12 h-28" />
              <Link
                to="/licencias"
                className="pulse-cta block rounded-[20px] bg-cian-300 px-4 py-2 text-white no-underline hover:brightness-105"
              >
                <b>¡INSCRÍBETE HOY!</b>
                <span className="block text-xs">Ver licencias</span>
              </Link>
            </div>
          )}

          <img
            src="/img/isotipo-cerebro-azul.png"
            alt=""
            className="solo-claro h-14 w-auto opacity-80"
          />
          <img
            src="/img/isotipo-cerebro-blanco.png"
            alt=""
            className="solo-oscuro h-14 w-auto opacity-80"
          />

          <div>
            <h3 className="font-bold text-azul-texto">Número de casos clínicos:</h3>
            {demo && (
              <p className="mt-1 text-xs text-texto-2">
                Simulador de demostración: 5 casos en modo entrenamiento.
              </p>
            )}
          </div>

          <div className="flex items-center gap-4">
            <button
              type="button"
              className="text-azul-texto transition-transform active:scale-90 disabled:opacity-40"
              aria-label="Menos casos"
              disabled={demo || paso === 0}
              onClick={() => setPaso((p) => Math.max(p - 1, 0))}
            >
              <MinusCircle size={40} strokeWidth={2.25} />
            </button>
            <output
              className="min-w-32 rounded-md border-[3px] border-verde-500 bg-lectura py-2 text-center text-3xl font-extrabold text-azul-texto tabular"
              aria-live="polite"
            >
              {casos}
            </output>
            <div className="relative">
              <button
                type="button"
                className="text-azul-texto transition-transform active:scale-90 disabled:opacity-40"
                aria-label="Más casos"
                disabled={demo || paso >= config.caseSteps.length - 1}
                onClick={() => setPaso((p) => Math.min(p + 1, config.caseSteps.length - 1))}
              >
                <PlusCircle size={40} strokeWidth={2.25} />
              </button>
              {demo && (
                <Link
                  to="/licencias"
                  className="insignia insignia-exclusivo absolute -top-4 -right-8 no-underline"
                >
                  Exclusivo
                </Link>
              )}
            </div>
          </div>
          <p className="max-w-xs text-xs font-bold text-texto-3">
            Algunos casos incluyen preguntas subsecuentes. Calcula 1 minuto por caso.
          </p>

          <button
            type="button"
            className="btn-cs rounded-md bg-texto-3/70 px-3 py-1 text-xs text-white hover:bg-texto-3"
            onClick={() => setParams({ historial: '1' })}
          >
            <History size={14} /> Historial del Simulador ENARM
          </button>

          {faltan && especialidades.length > 0 && (
            <p className="text-sm font-bold text-rojo-texto">
              Solo hay {disponibles} casos en esas especialidades: selecciona otra especialidad o
              reduce el número de casos.
            </p>
          )}
          <BotonBiColor
            icono={<Play size={28} />}
            activo
            variante="verde"
            disabled={faltan || crear.isPending}
            onClick={iniciar}
          >
            {crear.isPending ? 'Preparando…' : 'Iniciar'}
          </BotonBiColor>
        </section>

        <section data-card className="panel-vidrio space-y-4 text-center">
          <TituloBloque avatar={AVATARES.doctora}>3. Elige una CATEGORÍA</TituloBloque>
          {CATEGORIAS.map((c) => (
            <div key={c.key} className="space-y-2 pt-2">
              <BotonBiColor
                icono={<c.icono size={30} />}
                activo={catEf === c.key}
                disabled={demo && c.key !== 'aleatorio'}
                exclusivo={demo && c.key !== 'aleatorio'}
                onClick={() => {
                  setCategoria(c.key);
                  if (c.key === 'por-especialidad') setElegir(true);
                }}
              >
                {c.titulo}
              </BotonBiColor>
              <p className="mx-auto max-w-[260px] text-left text-sm text-texto-2">
                {c.key === 'por-especialidad' && especialidades.length > 0
                  ? `${String(especialidades.length)} seleccionada(s) · ${String(disponibles)} casos disponibles`
                  : c.texto}
              </p>
            </div>
          ))}
        </section>
      </div>

      <Dialog open={elegir} onOpenChange={setElegir}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogTitle>Elige las especialidades</DialogTitle>
          <DialogDescription>El simulador toma casos al azar de las que marques.</DialogDescription>
          <div className="mt-4 space-y-4">
            {config.areas.map((a) => (
              <fieldset key={a.key}>
                <legend className="text-sm font-extrabold text-verde-texto uppercase">
                  {a.name}
                </legend>
                <div className="mt-2 flex flex-wrap gap-2">
                  {a.specialties.map((s) => {
                    const on = especialidades.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        aria-pressed={on}
                        className="btn-modulo text-xs"
                        data-state={on ? 'active' : undefined}
                        onClick={() =>
                          setEspecialidades((prev) =>
                            on ? prev.filter((x) => x !== s.id) : [...prev, s.id],
                          )
                        }
                      >
                        {s.name} ({s.available})
                      </button>
                    );
                  })}
                  {a.specialties.length === 0 && (
                    <span className="text-xs text-texto-3">Sin casos disponibles.</span>
                  )}
                </div>
              </fieldset>
            ))}
          </div>
          <div className="mt-6 text-right">
            <button type="button" className="btn-cs btn-primario" onClick={() => setElegir(false)}>
              Listo
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={historial}
        onOpenChange={(o) => {
          if (!o) setParams({});
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
          <DialogTitle>Mis simuladores</DialogTitle>
          {historial && <Historial />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Historial() {
  const { data, error, isPending } = useHistorialSimulador();
  const importar = useImportarSimulador();
  const navigate = useNavigate();
  if (isPending) return <div className="mt-4 h-40 animate-pulse rounded-xl bg-white/5" />;
  if (error) return <p className="mt-4 text-rojo-texto">{mensajeDe(error)}</p>;
  if (data.length === 0)
    return <p className="mt-4 text-texto-2">Aún no has resuelto simuladores.</p>;

  const abrir = (h: HistoryItem) => {
    const ir = (id: number) =>
      void navigate(
        h.status === 'finished'
          ? `/simulador/${String(id)}/resultados`
          : `/simulador/${String(id)}`,
      );
    if (h.id !== null) ir(h.id);
    else
      importar.mutate(h.name, {
        onSuccess: (r) => ir(r.id),
        onError: (e) => toast.error(mensajeDe(e)),
      });
  };
  return (
    <div className="mt-4 overflow-x-auto">
      <table className="tabla-cs">
        <thead>
          <tr>
            <th>Fecha</th>
            <th>Modo</th>
            <th>Categoría</th>
            <th>Preguntas</th>
            <th>Correctas</th>
            <th>Calificación</th>
            <th>Estado</th>
          </tr>
        </thead>
        <tbody>
          {data.map((h) => (
            <tr key={h.name}>
              <td className="whitespace-nowrap">{fechaLarga(h.date)}</td>
              <td className="capitalize">{h.mode}</td>
              <td>{etiquetaCategoria(h.category)}</td>
              <td className="tabular">{h.questions}</td>
              <td className="tabular">{h.correct}</td>
              <td>
                <span className={cn('font-extrabold tabular', colorCalificacion(h.percent))}>
                  {h.percent}%
                </span>
              </td>
              <td>
                {h.source === 'legacy_v1' ? (
                  <span className="text-xs text-texto-3">Simulador anterior</span>
                ) : (
                  <button
                    type="button"
                    className="font-bold text-azul-texto underline disabled:opacity-50"
                    disabled={importar.isPending}
                    onClick={() => abrir(h)}
                  >
                    {h.status === 'finished' ? 'Resultados' : 'Reanudar'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
